import { navigation } from '@/app'
import { analytics } from '@/services'
import { Chapter, Module, Strategy } from '@/types'
import { navigateToHurdle, openURL, resolveStrategyAction } from '@/utils'
import { navigate } from 'gatsby'
import { useCallback, useMemo } from 'react'
import { tryOpenLesson } from '../utils'

type StrategyParams = Module & { onboardingLesson: boolean }

function isChapter(item: unknown): item is Chapter {
  return (item as Chapter).pages !== undefined
}

function openChapter(chapter: Chapter) {
  analytics.track('lesson_view', { lesson_name: chapter.title })
  tryOpenLesson(chapter.id)
}

function open(item: StrategyParams | Chapter) {
  if (isChapter(item)) {
    return openChapter(item)
  }

  if (item?.chapters?.length === 1) {
    return openChapter(item.chapters[0])
  }

  analytics.track('collection_view', { collection_name: item.title })
  navigate(`/learn/chapter/${item?.id}` as never)
}

function handleNavigateToHurdle() {
  navigateToHurdle()
  analytics.track('hurdle_start')
}

export function useOpenModule() {
  const strategies = useMemo(() => {
    return [
      {
        matches: item => item.type === 'lesson_group',
        execute: item => navigation.navigate('Learn.Content', { id: item.id }),
        name: 'lesson_group',
      },
      {
        matches: item => item.title.toLowerCase() === 'hurdle',
        execute: handleNavigateToHurdle,
        name: 'hurdle',
      },
      {
        matches: item => !!(item.beta_game && item.url),
        execute: item => openURL(item.url),
        name: 'external_link_game',
      },
      {
        matches: item => item.is_free,
        execute: open,
        name: 'free',
      },
      {
        matches: item => item.onboardingLesson,
        execute: open,
        name: 'onboarding_lesson',
      },
      {
        matches: item => item.is_placeholder,
        execute: () => {},
        name: 'placeholder',
      },
      {
        matches: () => true,
        execute: open,
        name: 'default',
      },
    ] satisfies Strategy<StrategyParams>[]
  }, [])

  const resolveModuleAction = useCallback(
    (item: StrategyParams) => {
      return resolveStrategyAction(item, strategies, 'useOpenModule')
    },
    [strategies],
  )

  return { resolveModuleAction, openModule: open }
}
